// The badge a `[[type:id]]` token becomes: a quiet chip with the entity's
// name that links to its record. Until the name resolves, or outside a run,
// it shows the entity type so the sentence still reads.
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { CalendarClock, Maximize2 } from 'lucide-react';
import {
    type ComponentProps,
    Fragment,
    type ReactNode,
    type SyntheticEvent,
    useState,
} from 'react';
import { type Components } from 'react-markdown';

import { MarkdownAnchor } from '@apps/web/components/ui/markdown';
import {
    REFERENCE_PILL_CLASS,
    ReferencePillContent,
    WRAPPING_REFERENCE_PILL_CLASS,
} from '@apps/web/components/ui/reference_pill';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    type ResolvedGroupMember,
    useEntityReferenceCitationDetail,
    useEntityReferenceLabel,
    useResolvedEntityReference,
} from '@apps/web/features/phoebe_chat_v3/api/entity_references';
import {
    isSourceCitationType,
    parseReferenceHref,
    parseReferenceTokens,
    referenceFallbackLabel,
    referenceRecordHref,
    type ReferenceType,
} from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { useEntityDetailHandlers } from '@apps/web/features/phoebe_chat_v3/ui/entity_detail_context';
import { useHarnessScope } from '@apps/web/features/phoebe_chat_v3/ui/harness_scope_context';
import { useInAttachmentPillBand } from '@apps/web/features/phoebe_chat_v3/ui/primitives/attachment_pill_band';
import { SourceCitationPreview } from '@apps/web/features/phoebe_chat_v3/ui/primitives/source_citation_preview';
import {
    INLINE_SOURCE_CITATION_CLASSES,
    REFERENCE_TYPE_HOVER_CLASSES,
    REFERENCE_TYPE_ICON_CLASSES,
    REFERENCE_TYPE_ICONS,
    REFERENCE_TYPE_TINT_CLASSES,
    SOURCE_CITATION_CLASSES,
} from '@apps/web/features/phoebe_chat_v3/ui/reference_icons';
import { useShiftReferenceChipClass } from '@apps/web/features/phoebe_chat_v3/ui/shift_reference_palette';
import { groupModeLabel } from '@apps/web/lib/outreach_shift_ordering';
import { cn } from '@apps/web/lib/utils';

export type ReferenceBadgeProps = Readonly<{
    readonly type: ReferenceType;
    readonly id: string;
    /** Renders a shift badge with the clock icon of the composer's staging
     * chips. User-authored copy sets this: there a referenced shift reads
     * as something the sender attached, whether staged from the calendar
     * or @-mentioned. */
    /** The words the model wrapped in the token, printed verbatim and
     * underlined. Null means a bare token, which renders as a chip. */
    readonly phrase?: ReactNode;
    readonly attachedShift?: boolean;
}>;

// Caregiver, client, and shift references open the in-panel detail popup so
// the chat stays self-contained. Callouts have no in-panel detail and keep
// in-app navigation; the same fallback applies without a provider.
function useReferenceOpenDetail(
    type: ReferenceType,
    subjectName: string | null = null,
): ((id: string) => void) | undefined {
    const detailHandlers = useEntityDetailHandlers();
    switch (type) {
        case 'caregiver':
            return detailHandlers?.openCaregiver;
        case 'client':
            return detailHandlers?.openClient;
        case 'shift':
            return detailHandlers?.openShift;
        case 'playbook':
            return detailHandlers?.openPlaybook;
        case 'care_plan':
            return detailHandlers?.openCarePlan;
        case 'caregiver_memory':
            return detailHandlers === null
                ? undefined
                : (id) =>
                      detailHandlers.openMemory({
                          type: 'caregiver',
                          id,
                          name: subjectName ?? '',
                      });
        case 'client_memory':
            return detailHandlers === null
                ? undefined
                : (id) =>
                      detailHandlers.openMemory({
                          type: 'client',
                          id,
                          name: subjectName ?? '',
                      });
        // Pages of their own, not records to preview; a group has no page
        // at all and links to its outreach.
        case 'callout':
        case 'group':
            return undefined;
        default: {
            const exhaustiveType: never = type;
            return exhaustiveType;
        }
    }
}

// The one interactive wrapper behind every reference surface, so the pill
// and the plain link cannot drift on where a click goes: a button into the
// in-panel detail when the host provides one, else an anchor to the record.
// The button is a span with the button role: a <button> always lays out as
// one atomic box, which a wrapping label cannot break across lines from.
function ReferenceTarget({
    type,
    id,
    title,
    className,
    href,
    children,
}: Readonly<{
    readonly type: ReferenceType;
    readonly id: string;
    readonly title?: string;
    readonly className: string;
    /** A destination the token alone cannot name: a group's outreach. */
    readonly href?: string;
    readonly children: ReactNode;
}>): ReactNode {
    const openDetail = useReferenceOpenDetail(type);
    if (openDetail !== undefined) {
        const open = (event: SyntheticEvent) => {
            // References can sit inside clickable rows; opening the popup
            // should not also trigger the row.
            event.stopPropagation();
            openDetail(id);
        };
        return (
            <span
                role="button"
                tabIndex={0}
                title={title}
                onClick={open}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        // Space would otherwise scroll the transcript.
                        event.preventDefault();
                        open(event);
                    }
                }}
                className={cn(className, 'cursor-pointer')}
            >
                {children}
            </span>
        );
    }
    return (
        <a
            href={href ?? referenceRecordHref(type, id)}
            title={title}
            className={className}
        >
            {children}
        </a>
    );
}

export function ReferenceBadge({
    type,
    id,
    phrase = null,
    attachedShift = false,
}: ReferenceBadgeProps): ReactNode {
    const scope = useHarnessScope();
    const label =
        useEntityReferenceLabel(scope, type, id) ??
        referenceFallbackLabel(type);
    const citationDetail = useEntityReferenceCitationDetail(scope, type, id);
    const openCitation = useReferenceOpenDetail(type, citationDetail);
    if (isSourceCitationType(type)) {
        return (
            <SourceCitation
                type={type}
                id={id}
                label={label}
                detail={citationDetail}
                phrase={phrase}
                openDetail={
                    openCitation === undefined
                        ? undefined
                        : () => openCitation(id)
                }
                href={referenceRecordHref(type, id)}
            />
        );
    }
    return (
        <EntityPill
            type={type}
            id={id}
            label={label}
            attachedShift={attachedShift}
        />
    );
}

/** A cited source is a receipt on the claim before it: a quiet chip that
    names the source, and a hover card that names which one. */
function SourceCitation({
    type,
    id,
    label,
    detail,
    phrase,
    openDetail,
    href,
}: Readonly<{
    readonly type: ReferenceType;
    readonly id: string;
    readonly label: string;
    readonly detail: string | null;
    readonly phrase: ReactNode;
    readonly openDetail: (() => void) | undefined;
    readonly href: string;
}>): ReactNode {
    const Icon = REFERENCE_TYPE_ICONS[type];
    const isTrailing = phrase === null;
    // A chip names the source it vouches for; a source read inside a
    // sentence is already named by the sentence, so it names itself.
    const body = isTrailing ? (
        <Fragment>
            {/* A real text baseline, or the icon floats the chip off the
                line it sits on. */}
            <span aria-hidden="true" className="w-0 select-none">
                {'\u200b'}
            </span>
            <Icon
                aria-hidden="true"
                // size-3.5, not 1em like a pill: these glyphs carry
                // internal detail drawn for a 24px viewBox, and below
                // 12px their strokes merge. It still fits the line box.
                className={cn(
                    'size-3.5 shrink-0',
                    REFERENCE_TYPE_ICON_CLASSES[type],
                )}
            />
            <span className="truncate">{label}</span>
        </Fragment>
    ) : (
        phrase
    );
    const className = isTrailing
        ? SOURCE_CITATION_CLASSES
        : INLINE_SOURCE_CITATION_CLASSES;
    const trigger =
        openDetail !== undefined ? (
            <button
                type="button"
                onClick={(event) => {
                    event.stopPropagation();
                    openDetail();
                }}
                className={cn(className, 'cursor-pointer')}
            >
                {body}
            </button>
        ) : (
            <a href={href} className={className}>
                {body}
            </a>
        );
    // No `detail === null` bail-out: a source that has not resolved, or
    // resolved to nothing, still opens its card. Without one a dead
    // citation is indistinguishable from a working one.
    return (
        // Its own provider: citations render inside thinking blocks and tool
        // output that sit outside the message-level provider.
        <TooltipPrimitive.Provider delayDuration={200}>
            <TooltipPrimitive.Root>
                <TooltipPrimitive.Trigger asChild={true}>
                    {trigger}
                </TooltipPrimitive.Trigger>
                <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content
                        sideOffset={4}
                        className="animate-in fade-in-0 zoom-in-95 border-border bg-popover text-popover-foreground text-v3-chat-meta z-50 flex w-max max-w-72 flex-col gap-1 rounded-md border px-2.5 py-1.5 shadow-md"
                    >
                        <span className="flex items-center gap-1.5">
                            <Icon
                                aria-hidden="true"
                                className={cn(
                                    'size-3.5 shrink-0',
                                    REFERENCE_TYPE_ICON_CLASSES[type],
                                )}
                            />
                            <span className="grow truncate font-semibold">
                                {detail ?? label}
                            </span>
                            {openDetail !== undefined && (
                                // The card is a look; this is the way in to
                                // the record itself.
                                <button
                                    type="button"
                                    aria-label="Open the full record"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        openDetail();
                                    }}
                                    className="text-muted-foreground hover:text-foreground hover:bg-muted -mr-0.5 ml-2 shrink-0 cursor-pointer rounded p-0.5 transition-colors"
                                >
                                    <Maximize2 className="size-3.5" />
                                </button>
                            )}
                        </span>
                        <SourceCitationPreview type={type} id={id} />
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
}

// Enough shifts to preview an outreach or a group without the popover
// scrolling; the "+N more" line carries the rest of the count and expands
// the list in place.
const HOVER_PREVIEW_MAX_SHIFTS = 6;

const HOVER_CONTENT_CLASS =
    'bg-popover text-popover-foreground border-border flex max-w-80 flex-col items-start gap-1 border p-2 text-v3-chat shadow-md';
const HOVER_NOTE_CLASS = 'text-muted-foreground px-1 text-v3-chat-meta';

/** The preview's shift rows, capped behind a "+N more" line that expands
 * them in place. The resolve response itself caps how many shift ids it
 * carries, so a very large outreach can still end with a plain remainder
 * after expanding. The expanded state lives on the pill, not here: Radix
 * renders tooltip content twice (visible plus its hidden aria copy), and
 * both copies must agree. */
function ExpandableShiftPreview({
    rows,
    totalCount,
    expanded,
    onExpand,
}: Readonly<{
    /** One keyed row per shift the resolve response carried. */
    readonly rows: ReadonlyArray<ReactNode>;
    /** The true shift total, which can exceed `rows.length`. */
    readonly totalCount: number;
    readonly expanded: boolean;
    readonly onExpand: () => void;
}>): ReactNode {
    const visibleRows = expanded
        ? rows
        : rows.slice(0, HOVER_PREVIEW_MAX_SHIFTS);
    const hiddenCount = totalCount - visibleRows.length;
    return (
        <Fragment>
            {/* Its own scroll box, so an expanded list cannot outgrow the
                viewport while the header and footer lines stay in view. */}
            <div className="flex max-h-72 max-w-full flex-col items-start gap-1 overflow-y-auto">
                {visibleRows}
            </div>
            {hiddenCount > 0 &&
                (expanded ? (
                    <span className={HOVER_NOTE_CLASS}>
                        +{hiddenCount} more
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={(event) => {
                            // Previews can sit inside clickable rows;
                            // expanding should not also trigger them.
                            event.stopPropagation();
                            onExpand();
                        }}
                        className={cn(
                            HOVER_NOTE_CLASS,
                            'hover:text-foreground cursor-pointer transition-colors',
                        )}
                    >
                        +{hiddenCount} more
                    </button>
                ))}
        </Fragment>
    );
}

/** The same pill with a caller-supplied name, for surfaces (like generated
 * tables) whose data already carries the entity's label. */
export function EntityPill({
    type,
    id,
    label,
    attachedShift = false,
}: Readonly<{
    readonly type: ReferenceType;
    readonly id: string;
    readonly label: string;
    readonly attachedShift?: ReferenceBadgeProps['attachedShift'];
}>): ReactNode {
    // The outreach variant lives in its own component so its entity-store
    // subscription (for the hover preview) never rides along on the other
    // pill types; plain pills stay presentation-only.
    if (type === 'callout') {
        return <OutreachEntityPill id={id} label={label} />;
    }
    if (type === 'group') {
        return <GroupEntityPill id={id} label={label} />;
    }
    return (
        <PlainEntityPill
            type={type}
            id={id}
            label={label}
            attachedShift={attachedShift}
        />
    );
}

function PlainEntityPill({
    type,
    id,
    label,
    attachedShift = false,
    suppressNativeTitle = false,
    href,
}: Readonly<{
    readonly type: ReferenceType;
    readonly id: string;
    readonly label: string;
    readonly attachedShift?: ReferenceBadgeProps['attachedShift'];
    /** Set by the outreach pill when its hover preview replaces the native
     * title tooltip, which would otherwise race it with the same text. */
    readonly suppressNativeTitle?: boolean;
    readonly href?: string;
}>): ReactNode {
    const shiftChipClass = useShiftReferenceChipClass(type, id);
    const inPillBand = useInAttachmentPillBand();
    // Shift and callout labels carry full time ranges, worth wrapping
    // across lines in prose. Inside an attachment pill band the pill must
    // stay one line to keep the band's height bounded; the full label
    // remains in the hover title.
    const wraps = (type === 'shift' || type === 'callout') && !inPillBand;
    const isAttachedShift = attachedShift && type === 'shift';
    const Icon = isAttachedShift ? CalendarClock : REFERENCE_TYPE_ICONS[type];
    const body = (
        <ReferencePillContent Icon={Icon} label={label} wrap={wraps} />
    );
    const tintClass =
        shiftChipClass ??
        cn(
            REFERENCE_TYPE_TINT_CLASSES[type],
            REFERENCE_TYPE_HOVER_CLASSES[type],
        );
    return (
        <ReferenceTarget
            type={type}
            id={id}
            title={suppressNativeTitle ? undefined : label}
            href={href}
            className={cn(
                wraps ? WRAPPING_REFERENCE_PILL_CLASS : REFERENCE_PILL_CLASS,
                tintClass,
            )}
        >
            {body}
        </ReferenceTarget>
    );
}

/** The outreach pill: the plain pill plus a hover popover previewing the
 * outreach's shifts. The ids ride the same resolve response as the badge's
 * own label, so the popover needs no extra endpoint; the shift pills
 * inside resolve themselves on mount. */
function OutreachEntityPill({
    id,
    label,
}: Readonly<{
    readonly id: string;
    readonly label: string;
}>): ReactNode {
    const scope = useHarnessScope();
    const reference = useResolvedEntityReference(scope, 'callout', id);
    const [previewExpanded, setPreviewExpanded] = useState(false);
    const calloutPreview =
        reference !== null &&
        reference.kind === 'callout' &&
        reference.shiftIds.length > 0
            ? reference
            : null;
    const pill = (
        <PlainEntityPill
            type="callout"
            id={id}
            label={label}
            suppressNativeTitle={calloutPreview !== null}
        />
    );
    if (calloutPreview === null) {
        return pill;
    }
    return (
        <HoverPreview pill={pill}>
            <ExpandableShiftPreview
                rows={calloutPreview.shiftIds.map((shiftId) => (
                    <ReferenceBadge key={shiftId} type="shift" id={shiftId} />
                ))}
                totalCount={calloutPreview.shiftCount}
                expanded={previewExpanded}
                onExpand={() => setPreviewExpanded(true)}
            />
        </HoverPreview>
    );
}

function HoverPreview({
    pill,
    children,
}: Readonly<{
    readonly pill: ReactNode;
    readonly children: ReactNode;
}>): ReactNode {
    return (
        <Tooltip>
            {/* An element of its own for Radix to measure and hover-track.
                It stays inline so a wrapping pill inside still breaks with
                the sentence. */}
            <TooltipTrigger asChild={true}>
                <span>{pill}</span>
            </TooltipTrigger>
            <TooltipContent
                side="bottom"
                align="start"
                hideArrow={true}
                className={HOVER_CONTENT_CLASS}
            >
                {children}
            </TooltipContent>
        </Tooltip>
    );
}

function memberOutcomeLabel(member: ResolvedGroupMember): string | null {
    switch (member.resolution) {
        case 'filled':
            return member.assignedCaregiverName === null
                ? 'filled'
                : `filled · ${member.assignedCaregiverName}`;
        case 'cancelled':
            return 'cancelled';
        default:
            return null;
    }
}

/** A group has no single owner, so each caregiver's share is the fact. */
function assignedCaregiverLines(
    members: ReadonlyArray<ResolvedGroupMember>,
): ReadonlyArray<string> {
    const countByName = new Map<string, number>();
    for (const member of members) {
        const name = member.assignedCaregiverName;
        if (member.resolution !== 'filled' || name === null) {
            continue;
        }
        countByName.set(name, (countByName.get(name) ?? 0) + 1);
    }
    return [...countByName].map(
        ([name, count]) => `${name} · ${count} of ${members.length}`,
    );
}

/** The group pill: the plain pill plus a hover popover listing every member
 * shift. A group has no page, so the pill links to its outreach. */
function GroupEntityPill({
    id,
    label,
}: Readonly<{
    readonly id: string;
    readonly label: string;
}>): ReactNode {
    const scope = useHarnessScope();
    const reference = useResolvedEntityReference(scope, 'group', id);
    const [previewExpanded, setPreviewExpanded] = useState(false);
    const group =
        reference !== null && reference.kind === 'group' ? reference : null;
    const pill = (
        <PlainEntityPill
            type="group"
            id={id}
            label={label}
            suppressNativeTitle={group !== null}
            href={
                group === null ? undefined : `/outreaches/${group.outreachId}`
            }
        />
    );
    if (group === null) {
        return pill;
    }
    const members = group.members;
    const header =
        `${groupModeLabel(group.groupMode)} · ${members.length} ${members.length === 1 ? 'shift' : 'shifts'}` +
        (group.groupMode === 'all' && !group.allOrNoneActive
            ? ' (no longer all-or-none)'
            : '');
    return (
        <HoverPreview pill={pill}>
            <span className={HOVER_NOTE_CLASS}>{header}</span>
            <ExpandableShiftPreview
                rows={members.map((member) => {
                    const outcome = memberOutcomeLabel(member);
                    return (
                        <span
                            key={member.shiftId}
                            className={cn(
                                'flex max-w-full items-center gap-1',
                                outcome !== null && 'opacity-60',
                            )}
                        >
                            <ReferenceBadge type="shift" id={member.shiftId} />
                            {outcome !== null && (
                                <span className="text-muted-foreground text-v3-chat-meta shrink-0">
                                    {outcome}
                                </span>
                            )}
                        </span>
                    );
                })}
                totalCount={members.length}
                expanded={previewExpanded}
                onExpand={() => setPreviewExpanded(true)}
            />
            {assignedCaregiverLines(members).map((line) => (
                <span key={line} className={HOVER_NOTE_CLASS}>
                    {line}
                </span>
            ))}
        </HoverPreview>
    );
}

// Plain text that opens the same detail or record as the pill: the name
// sits in the sentence like any other word, a shade darker than muted
// narration, and underlines on hover.
const LINK_CLASS =
    'focus-visible:outline-agent-action text-foreground rounded-sm underline-offset-2 outline-none hover:underline focus-visible:outline-2 focus-visible:outline-offset-2';

/** The reference as ordinary clickable text rather than a chip. Event lines
 * use it so narration reads as a sentence instead of a row of pills. */
export function ReferenceLink({
    type,
    id,
}: Readonly<{
    readonly type: ReferenceType;
    readonly id: string;
}>): ReactNode {
    const scope = useHarnessScope();
    const label =
        useEntityReferenceLabel(scope, type, id) ??
        referenceFallbackLabel(type);
    return (
        <ReferenceTarget type={type} id={id} className={LINK_CLASS}>
            {label}
        </ReferenceTarget>
    );
}

/** Plain text with badges in place of tokens, for the user bubble, the
    queued-message stack, and agent working-on copy. Only user-authored
    surfaces opt into the attached-shift presentation; system copy keeps
    the generic shift badge. */
export function ReferenceText({
    text,
    attachedShifts = false,
}: Readonly<{
    readonly text: string;
    /** Renders shift badges with the attached-shift clock icon; set by
     * user-authored surfaces where a referenced shift is one the sender
     * brought into the conversation. */
    readonly attachedShifts?: boolean;
}>): ReactNode {
    return (
        <Fragment>
            {parseReferenceTokens(text).map((segment, index) =>
                segment.kind === 'text' ? (
                    // Segments never reorder, so the index is a stable key.
                    <Fragment key={index}>{segment.text}</Fragment>
                ) : (
                    <ReferenceBadge
                        key={index}
                        type={segment.type}
                        id={segment.id}
                        phrase={segment.phrase}
                        attachedShift={attachedShifts}
                    />
                ),
            )}
        </Fragment>
    );
}

function ReferenceName({
    type,
    id,
}: Readonly<{
    readonly type: ReferenceType;
    readonly id: string;
}>): ReactNode {
    const scope = useHarnessScope();
    return (
        useEntityReferenceLabel(scope, type, id) ?? referenceFallbackLabel(type)
    );
}

/** Plain name resolution for card copy, including text nested in buttons.
 * Unlike `ReferenceText`, this never introduces an interactive badge. */
export function ResolvedReferenceText({
    text,
}: Readonly<{ readonly text: string }>): ReactNode {
    return (
        <Fragment>
            {parseReferenceTokens(text).map((segment, index) => {
                if (segment.kind === 'text') {
                    return <Fragment key={index}>{segment.text}</Fragment>;
                }
                // A wrapped phrase is the sentence's own words; a
                // resolved label here would rewrite the sentence.
                if (segment.phrase !== null) {
                    return <Fragment key={index}>{segment.phrase}</Fragment>;
                }
                return (
                    <ReferenceName
                        key={index}
                        type={segment.type}
                        id={segment.id}
                    />
                );
            })}
        </Fragment>
    );
}

function ReferenceAwareAnchor({
    href,
    children,
    ...props
}: ComponentProps<typeof MarkdownAnchor>): ReactNode {
    const reference = parseReferenceHref(href);
    if (reference !== null) {
        return (
            <ReferenceBadge
                type={reference.type}
                id={reference.id}
                // The model's own words when it wrapped them; the renderer
                // prints them and never writes any of its own.
                phrase={reference.anchored ? children : null}
            />
        );
    }
    return (
        <MarkdownAnchor href={href} {...props}>
            {children}
        </MarkdownAnchor>
    );
}

/** Markdown renderers that turn reference links into badges. */
export const REFERENCE_MARKDOWN_COMPONENTS: Components = {
    a: ReferenceAwareAnchor,
};
